import React, { useEffect } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Box, Button, CircularProgress, Grid, Typography } from '@material-ui/core';
import { PveQuery, PveData, PveVars, PveChangedSubscription, PveChangedData, PveChangedVars } from '@pvplevelup/core/dist/queries/pve/pve';
import { GameStatus } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import { PveRunView } from './PveRunView';
import { RecordVideos } from 'components/Video/RecordVideos';

/** Loads a run and, while it's in progress, keeps it updated from the server. */
export function usePveRun(id: string | null | undefined) {
  const { data, loading, error, subscribeToMore } = useQuery<PveData, PveVars>(PveQuery, {
    variables: {id: id || ''},
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });
  const pve = data?.pve;
  const live = pve?.status == GameStatus.Active;

  useEffect(() => {
    if(!live || !id)
      return;
    return subscribeToMore<PveChangedData, PveChangedVars>({
      document: PveChangedSubscription,
      variables: {id},
      updateQuery: (prev, {subscriptionData}) => {
        const changed = subscriptionData.data?.pveChanged?.pve;
        return changed ? {...prev, pve: changed} : prev;
      },
    });
  }, [id, live]);

  return {pve, loading, error};
}

interface RouteParams {
  id: string;
}

/** A dungeon / raid run: live while it's going, the full breakdown once it's over. */
export const PveRunPage: React.FC<RouteComponentProps<RouteParams>> = (props) => {
  const history = useHistory();
  const {pve, loading, error} = usePveRun(props.match.params.id);

  return (
    <Grid container spacing={1} className="content-container">
      <Grid item xs={12}>
        <Box mb={1}>
          <Button size="small" onClick={() => history.goBack()}>Back</Button>
        </Box>
        {pve && <PveRunView pve={pve} />}
        {pve && <RecordVideos entries={(pve.players || []).map((p: any) => ({user: p.user, name: p.name, video: p.video}))} />}
        {!pve && loading && <Box p={4} textAlign="center"><CircularProgress /></Box>}
        {!pve && !loading && <Typography style={{opacity: 0.7}}>{error ? error.message : 'Run not found.'}</Typography>}
      </Grid>
    </Grid>
  );
}
